import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { apis } from '@/modules/apiClients'
import { adminUserDetailsQueryKey } from './useUserDetails'

/**
 * Bulk-resolve user-account `createdAt` (the real "joined" date) by userId, via the
 * partner-accessible user-details endpoint — one cached query per id, sharing cache with
 * `useUserDetails` (so an expanded row reuses what this prefetched). Returns a map
 * userId -> createdAt, populated as each query resolves. Pass only non-empty userIds.
 */
export const useUsersCreatedAt = (userIds: string[]): Record<string, string> => {
  const results = useQueries({
    queries: userIds.map((userId) => ({
      queryKey: adminUserDetailsQueryKey(userId),
      queryFn: async () => (await apis.userService.adminUserDetailsId(userId)).data,
      enabled: userId !== '',
    })),
  })

  // Stable signature so the returned map keeps identity until a createdAt actually resolves.
  const signature = userIds.map((id, i) => `${id}:${results[i]?.data?.user?.createdAt ?? ''}`).join('|')

  return useMemo(() => {
    const map: Record<string, string> = {}
    userIds.forEach((id, i) => {
      const createdAt = results[i]?.data?.user?.createdAt
      if (createdAt !== undefined && createdAt !== null && createdAt !== '') map[id] = createdAt
    })
    return map
    // Depend on `signature` only: it captures the sole relevant change in `results`/`userIds`,
    // keeping the returned map's identity stable until a createdAt actually resolves.
  }, [signature])
}
